// src/pages/Admin.tsx - Integration der Category Suggestion Manager Komponente
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, isAdmin as checkIsAdmin } from '../services/authService';
import LoadingState from '../components/common/LoadingState';
import IconManager from '../components/admin/IconManager';
import AdminAchievementCategorizer from '../components/admin/AdminAchievementCategorizer';
import CategorySuggestionManager from '../components/admin/CategorySuggestionManager';

const Admin: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [activeSection, setActiveSection] = useState<string>('dashboard');

  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        if (!isAuthenticated()) {
          navigate('/login', { state: { returnPath: '/admin' } });
          return;
        }

        // Check if user is admin
        const hasAdminRights = checkIsAdmin();
        setIsAdmin(hasAdminRights);

        if (!hasAdminRights) {
          setError('You do not have administrator rights');
        }
      } catch (err) {
        console.error('Error checking admin access:', err);
        setError('An error occurred while checking your access rights');
      }
    };

    checkAdminAccess();
  }, [navigate]);

  if (!isAdmin && !loading) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-bold text-destiny-red mb-4">Access Denied</h2>
        <p className="text-gray-300 mb-4">You do not have administrator rights to access this page.</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-destiny-gold mb-6">Admin Dashboard</h1>
      
      {error && (
        <div className="bg-destiny-red bg-opacity-20 border border-destiny-red rounded-lg p-4 mb-6">
          <h3 className="text-destiny-red font-medium mb-2">Error</h3>
          <p className="text-gray-300">{error}</p>
          <button 
            onClick={() => setError(null)}
            className="mt-2 text-white bg-destiny-red bg-opacity-30 hover:bg-opacity-50 px-3 py-1 rounded text-sm"
          >
            Dismiss
          </button>
        </div>
      )}
      
      {/* Navigation Tabs - Add a new tab for Category Suggestions */}
      <div className="mb-6 border-b border-gray-700">
        <div className="flex space-x-1 overflow-x-auto pb-1">
          <button
            onClick={() => setActiveSection('dashboard')}
            className={`px-4 py-2 font-medium rounded-t-lg ${
              activeSection === 'dashboard'
                ? 'bg-gray-800 text-destiny-gold border-t border-l border-r border-gray-700'
                : 'bg-gray-700 text-gray-300 hover:text-white'
            }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setActiveSection('icon-manager')}
            className={`px-4 py-2 font-medium rounded-t-lg ${
              activeSection === 'icon-manager'
                ? 'bg-gray-800 text-destiny-gold border-t border-l border-r border-gray-700'
                : 'bg-gray-700 text-gray-300 hover:text-white'
            }`}
          >
            Icon Manager
          </button>
          <button
            onClick={() => setActiveSection('categorizer')}
            className={`px-4 py-2 font-medium rounded-t-lg ${
              activeSection === 'categorizer'
                ? 'bg-gray-800 text-destiny-gold border-t border-l border-r border-gray-700'
                : 'bg-gray-700 text-gray-300 hover:text-white'
            }`}
          >
            Categorizer
          </button>
          <button
            onClick={() => setActiveSection('suggestions')}
            className={`px-4 py-2 font-medium rounded-t-lg ${
              activeSection === 'suggestions'
                ? 'bg-gray-800 text-destiny-gold border-t border-l border-r border-gray-700'
                : 'bg-gray-700 text-gray-300 hover:text-white'
            }`}
          >
            Suggestions
          </button>
          <button
            onClick={() => navigate('/admin/achievements')}
            className="px-4 py-2 font-medium rounded-t-lg bg-gray-700 text-gray-300 hover:text-white"
          >
            Achievements
          </button>
          <button
            onClick={() => navigate('/admin/translations')}
            className="px-4 py-2 font-medium rounded-t-lg bg-gray-700 text-gray-300 hover:text-white"
          >
            Translations
          </button>
        </div>
      </div>
      
      {/* Content Section */}
      {loading ? (
        <LoadingState message="Loading..." />
      ) : (
        <>
          {activeSection === 'dashboard' && (
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold text-white mb-4">Dashboard</h2>
              {/* Dashboard content here */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-700 p-4 rounded-lg">
                  <h3 className="text-destiny-gold font-medium mb-2">Quick Actions</h3>
                  <div className="space-y-2">
                    <button 
                      onClick={() => setActiveSection('icon-manager')}
                      className="w-full text-left px-3 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded"
                    >
                      Manage Missing Icons
                    </button>
                    <button 
                      onClick={() => setActiveSection('categorizer')}
                      className="w-full text-left px-3 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded"
                    >
                      Categorize Achievements
                    </button>
                    <button 
                      onClick={() => setActiveSection('suggestions')}
                      className="w-full text-left px-3 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded"
                    >
                      Configure Achievement Suggestions
                    </button>
                    <button 
                      onClick={() => navigate('/admin/achievements')}
                      className="w-full text-left px-3 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded"
                    >
                      Manage Achievements
                    </button>
                    <button 
                      onClick={() => navigate('/admin/translations')}
                      className="w-full text-left px-3 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded"
                    >
                      Manage Translations
                    </button>
                  </div>
                </div>
                
                <div className="bg-gray-700 p-4 rounded-lg">
                  <h3 className="text-destiny-gold font-medium mb-2">Database Information</h3>
                  <p className="text-gray-300">
                    Access database statistics and maintenance tools from the appropriate sections.
                  </p>
                  <div className="mt-4 bg-gray-600 p-3 rounded text-sm">
                    <strong className="text-destiny-gold">System Status:</strong>
                    <ul className="mt-2 space-y-1 text-gray-300">
                      <li>✅ Application running normally</li>
                      <li>✅ Database connection established</li>
                      <li>✅ API endpoints responding</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeSection === 'icon-manager' && (
            <IconManager />
          )}
          
          {activeSection === 'categorizer' && (
            <AdminAchievementCategorizer />
          )}
          
          {/* Neue Sektion für den Category Suggestion Manager */}
          {activeSection === 'suggestions' && (
            <CategorySuggestionManager />
          )}
        </>
      )}
    </div>
  );
};

export default Admin;